/**
 * StepDetail – Full detail view for a single settlement step.
 * Renders Point/Goal guidance, sub-tasks with structured form fields, and checklists.
 */

import { useState, useEffect } from 'react';
import ChecklistItem from './ChecklistItem';
import type { SettlementFormField, SettlementStep } from '../../utils/settlementFlowConfig';
import { SETTLEMENT_PLANNER_LAYOUT, SETTLEMENT_PLANNER_PROGRESS } from './settlementTheme';

// Suggest Type based on step and subtask context for the checklist item
function getSuggestedTrackerTypeForItem(stepId: string, itemId?: string): string {
  // Extract subtask from itemId: not-1-a-1 → not-1-a
  if (itemId) {
    const parts = itemId.split('-');
    if (parts.length >= 3) {
      const subtaskId = `${parts[0]}-${parts[1]}-${parts[2]}`;
      
      // not-1-a: Government agencies → Government Agency
      if (subtaskId === 'not-1-a') return 'Government Agency';
      // not-1-b: Notify known creditors → Service / Subscription (creditors are entities)
      if (subtaskId === 'not-1-b') return 'Service / Subscription';
      
      // not-2-a: Financial institutions → Financial Institution
      if (subtaskId === 'not-2-a') return 'Financial Institution';
      // not-2-b: Insurance and employment benefits → Financial Institution (broad category)
      if (subtaskId === 'not-2-b') return 'Financial Institution';
      
      // not-3-a: Employer notices → Employer / Business
      if (subtaskId === 'not-3-a') return 'Employer / Business';
      // not-3-b: Mail and subscriptions → Service / Subscription
      if (subtaskId === 'not-3-b') return 'Service / Subscription';
      // not-3-c: Digital and utilities → Service / Subscription
      if (subtaskId === 'not-3-c') return 'Service / Subscription';
    }
  }
  
  // Fallback to step-level suggestion
  if (stepId === 'not-1') return 'Government Agency';
  if (stepId === 'not-2') return 'Financial Institution';
  if (stepId === 'not-3') return 'Employer / Business';
  return ''; // No suggestion for other steps
}

interface StepDetailProps {
  step: SettlementStep;
  completedSubTaskIds: Set<string>;
  completedChecklistItemIds: Set<string>;
  onToggleChecklistItem: (itemId: string, checked: boolean) => void;
  onToggleSubTask: (subTaskId: string, checked: boolean) => void;
  onBack: () => void;
  notes?: string;
  onNotesChange?: (notes: string) => void;
  /** Values for configured form fields (keyed by field id) */
  formValues?: Record<string, string>;
  onFormChange?: (fieldId: string, value: string) => void;
  disabled?: boolean;
  layoutVariant?: 'default' | 'planner';
  /** Count of tracked entries per checklist item id */
  trackedCounts?: Record<string, number>;
  /** Callback when user clicks + Add to Tracker for a checklist item */
  onAddToTracker?: (itemId: string, suggestedName?: string, suggestedType?: string) => void;
}

function GuidanceBlock({ title, guidance, isPlanner }: { title?: string; guidance: { point: string; goal: string }; isPlanner?: boolean }) {
  const cardClass = isPlanner ? SETTLEMENT_PLANNER_LAYOUT.guidanceCard : 'rounded-xl border border-blue-100 bg-blue-50/40 p-4 mb-4';
  const labelClass = isPlanner ? SETTLEMENT_PLANNER_LAYOUT.guidanceLabel : 'text-xs font-semibold text-blue-900 uppercase mb-1';
  const bodyClass = isPlanner ? SETTLEMENT_PLANNER_LAYOUT.guidanceBody : 'text-sm text-stone-800 leading-relaxed';

  return (
    <div className={cardClass}>
      {title && <p className={`${labelClass} mb-2`}>{title}</p>}
      <p className={labelClass}>Point</p>
      <p className={`${bodyClass} mb-3`}>{guidance.point}</p>
      <p className={labelClass}>Goal</p>
      <p className={bodyClass}>{guidance.goal}</p>
    </div>
  );
}

function FormFieldInput({
  field,
  value,
  onChange,
  disabled,
}: {
  field: SettlementFormField;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  const common =
    'w-full rounded-lg border border-stone-300 px-3 py-2 text-sm text-black placeholder:text-stone-500 focus:border-cyan-600 focus:ring-1 focus:ring-cyan-600 outline-none transition';

  if (field.type === 'textarea') {
    return (
      <label className="block">
        <span className="text-xs font-medium text-stone-700 mb-1 block">{field.label}</span>
        <textarea
          className={`${common} resize-y min-h-[72px]`}
          rows={field.rows ?? 3}
          placeholder={field.placeholder}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        />
        {field.hint && <p className="text-xs text-stone-500 mt-1">{field.hint}</p>}
      </label>
    );
  }

  if (field.type === 'select') {
    return (
      <label className="block">
        <span className="text-xs font-medium text-stone-700 mb-1 block">{field.label}</span>
        <select
          className={common}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        >
          {(field.options || []).map((opt) => (
            <option key={opt.value || 'empty'} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        {field.hint && <p className="text-xs text-stone-500 mt-1">{field.hint}</p>}
      </label>
    );
  }

  if (field.type === 'date') {
    return (
      <label className="block">
        <span className="text-xs font-medium text-stone-700 mb-1 block">{field.label}</span>
        <input
          type="date"
          className={common}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        />
        {field.hint && <p className="text-xs text-stone-500 mt-1">{field.hint}</p>}
      </label>
    );
  }

  return (
    <label className="block">
      <span className="text-xs font-medium text-stone-700 mb-1 block">{field.label}</span>
      <input
        type="text"
        className={common}
        placeholder={field.placeholder}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      />
      {field.hint && <p className="text-xs text-stone-500 mt-1">{field.hint}</p>}
    </label>
  );
}

export default function StepDetail({
  step,
  completedChecklistItemIds,
  completedSubTaskIds,
  onToggleChecklistItem,
  onToggleSubTask,
  onBack,
  notes,
  onNotesChange,
  formValues = {},
  onFormChange,
  disabled,
  layoutVariant = 'planner',
  trackedCounts = {},
  onAddToTracker,
}: StepDetailProps) {
  const [localNotes, setLocalNotes] = useState(notes || '');
  const isPlanner = layoutVariant === 'planner';
  const titleClassName = isPlanner ? SETTLEMENT_PLANNER_LAYOUT.title : 'font-display text-[28px] leading-tight text-blue-900';
  const descriptionClassName = isPlanner ? SETTLEMENT_PLANNER_LAYOUT.subtitle : "text-sm text-grey-700 leading-relaxed mb-4";
  const notesLabelClassName = isPlanner ? SETTLEMENT_PLANNER_LAYOUT.notesLabel : "text-sm font-semibold text-black uppercase tracking-wide mb-2";
  const notesInputClassName = isPlanner ? SETTLEMENT_PLANNER_LAYOUT.notesInput : "w-full rounded-xl border border-stone-300 px-4 py-3 text-sm text-black placeholder:text-stone-400 focus:border-cyan-600 focus:ring-1 focus:ring-cyan-600 outline-none transition resize-none min-h-[80px]";
  const subTaskCardClassName = isPlanner ? SETTLEMENT_PLANNER_LAYOUT.subTaskCard : 'rounded-xl border border-grey-200 bg-white-alt p-5 shadow-sm';
  const subTaskTitleClassName = isPlanner ? SETTLEMENT_PLANNER_LAYOUT.subTaskTitle : 'text-base font-semibold text-black';
  const subTaskDescClassName = isPlanner ? SETTLEMENT_PLANNER_LAYOUT.cardDescription : 'text-sm text-grey-600 mt-1 leading-relaxed';
  const doneButtonClassName = (complete: boolean) => isPlanner
    ? (complete ? SETTLEMENT_PLANNER_LAYOUT.doneButtonComplete : SETTLEMENT_PLANNER_LAYOUT.doneButtonDefault)
    : `text-xs font-semibold px-3 py-1.5 rounded-md border transition-colors ${complete ? 'border-green-300 bg-green-50 text-green-900' : 'border-grey-300 bg-white text-stone-700 hover:border-blue-300'}`;
  useEffect(() => {
    setLocalNotes(notes || '');
  }, [notes]);

  useEffect(() => {
    if (!onNotesChange) return;
    if (localNotes === (notes || '')) return;

    const timer = setTimeout(() => {
      onNotesChange(localNotes);
    }, 400);

    return () => clearTimeout(timer);
  }, [localNotes, notes, onNotesChange]);

  const subTasks = step.subTasks || [];
  const totalChecklistItems = subTasks.reduce(
    (sum, subTask) => sum + (subTask.checklistItems?.length || 0),
    0
  );
  const completedChecklistItems = completedChecklistItemIds.size;
  const checklistProgressPct = totalChecklistItems > 0
    ? Math.round((completedChecklistItems / totalChecklistItems) * 100)
    : 0;

  return (
    <div className={layoutVariant === 'planner' ? '' : 'max-w-3xl mx-auto'}>
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={onBack}
          className="w-[35px] h-[35px] flex items-center justify-center text-grey-600 hover:text-cyan-900 transition-colors"
          aria-label="Go back"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 className={titleClassName}>{step.title}</h1>
      </div>

      <p className={descriptionClassName}>{step.description}</p>

      {step.guidance && <GuidanceBlock guidance={step.guidance} isPlanner={isPlanner} />}

      {totalChecklistItems > 0 && (
        <div className={SETTLEMENT_PLANNER_PROGRESS.card + ' mb-6'}>
          <div className="flex items-center justify-between gap-3 mb-2">
            <p className={SETTLEMENT_PLANNER_PROGRESS.progressTitle}>Checklist progress</p>
            <p className={SETTLEMENT_PLANNER_PROGRESS.count}>
              {completedChecklistItems} of {totalChecklistItems}
            </p>
          </div>
          <div className={SETTLEMENT_PLANNER_PROGRESS.track}>
            <div
              className={SETTLEMENT_PLANNER_PROGRESS.fill}
              style={{ width: `${checklistProgressPct}%` }}
            />
          </div>
        </div>
      )}

      <div className="space-y-8 mb-8">
        {subTasks.map((sub) => {
          const items = sub.checklistItems || [];
          const fields = sub.formFields || [];
          const hasChecklist = items.length > 0;
          const subComplete = completedSubTaskIds.has(sub.id);

          return (
            <section
              key={sub.id}
              className={subTaskCardClassName}
            >
              <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                <div>
                  <h2 className={subTaskTitleClassName}>{sub.title}</h2>
                  {sub.description && (
                    <p className={subTaskDescClassName}>{sub.description}</p>
                  )}
                </div>
                {!hasChecklist && (
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => onToggleSubTask(sub.id, !subComplete)}
                    className={`${doneButtonClassName(subComplete)} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {subComplete ? 'Section marked done' : 'Mark section done'}
                  </button>
                )}
              </div>

              {sub.guidance && <GuidanceBlock title="Guidance" guidance={sub.guidance} isPlanner={isPlanner} />}

              {fields.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  {fields.map((field) => (
                    <div key={field.id} className={field.type === 'textarea' ? 'md:col-span-2' : ''}>
                      <FormFieldInput
                        field={field}
                        value={formValues[field.id] ?? ''}
                        disabled={disabled}
                        onChange={(v) => onFormChange?.(field.id, v)}
                      />
                    </div>
                  ))}
                </div>
              )}

              {hasChecklist && (
                <div className="space-y-3">
                  {items.map((item) => (
                    <ChecklistItem
                      key={item.id}
                      id={item.id}
                      title={item.title}
                      description={item.description}  
                      whatThisIs={item.whatThisIs}
                      whyItMatters={item.whyItMatters}
                      bulletPoints={item.bulletPoints}
                      checked={completedChecklistItemIds.has(item.id)}
                      onChange={onToggleChecklistItem}
                      disabled={disabled}
                      trackedCount={trackedCounts[item.id] || 0}
                      onAddToTracker={() => onAddToTracker?.(item.id, item.title, getSuggestedTrackerTypeForItem(step.id, item.id))}
                    />
                  ))}
                </div>
              )}

              {!hasChecklist && fields.length === 0 && !sub.description && (
                <p className="text-xs text-stone-500 italic">No interactive items configured for this block.</p>
              )}
            </section>
          );
        })}
      </div>

      {onNotesChange !== undefined && (
        <div className="mb-8">
          <h2 className={notesLabelClassName}>Notes</h2>
          <textarea
            value={localNotes}
            onChange={(e) => setLocalNotes(e.target.value)}
            disabled={disabled}
            placeholder="Add any notes about this step..."
            className={notesInputClassName}
            rows={3}
          />
        </div>
      )}
    </div>
  );
}
